import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { auditApi } from '@/services/api';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { useUIStore } from '@/store/uiStore';
import { Download, RefreshCw, FileText, Menu, Loader2 } from 'lucide-react';

const ACTION_TYPES = [
  'Sale',
  'Return',
  'DiscountOverride',
  'PriceChange',
  'Refund',
  'ShiftOpen',
  'ShiftClose',
  'CashMovement',
  'VarianceApproval',
  'Void',
  'DeleteSale',
] as const;

export default function ActivityLogPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const [actionType, setActionType] = useState('');
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['auditLogs', actionType],
    queryFn: async () => {
      const res = await auditApi.list({ action_type: actionType || undefined, limit: 200 });
      return res.data.message as Array<{
        name: string;
        timestamp: string;
        user: string;
        user_name: string;
        action_type: string;
        reference_doctype?: string;
        reference_name?: string;
        details?: string;
        pos_shift?: string;
      }>;
    },
  });

  const actionLabel = (type: string) =>
    t(`activityLog.actionTypes.${type}`, type);

  const exportCsv = () => {
    if (!data?.length) return;
    const header = [
      t('activityLog.time'),
      t('activityLog.user'),
      t('activityLog.action'),
      t('activityLog.reference'),
      'Details',
    ];
    const rows = data.map((r) => [
      r.timestamp,
      r.user_name || r.user,
      actionLabel(r.action_type),
      r.reference_name || '',
      typeof r.details === 'string' ? r.details : JSON.stringify(r.details || ''),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pos-activity-log.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto flex flex-col">
        <TrialBanner />
        <div className="p-6 flex-1 max-w-7xl mx-auto w-full">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={toggleSidebar}
                className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-150 hover:bg-gray-200 rounded-lg shrink-0"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-primary-600" />
                  {t('activityLog.title')}
                </h1>
                <p className="text-gray-500 text-sm">{t('activityLog.subtitle')}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void refetch()}
                className="btn-secondary inline-flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                {t('activityLog.refresh')}
              </button>
              <button
                type="button"
                onClick={exportCsv}
                disabled={!data?.length}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {t('activityLog.exportCsv')}
              </button>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col sm:flex-row gap-4 mb-6 items-end">
            <div className="flex-1 min-w-[200px] space-y-1.5">
              <label className="text-sm font-medium text-gray-700 block">
                {t('activityLog.filterAction')}
              </label>
              <select
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
                className="input-field"
              >
                <option value="">{t('activityLog.allActions')}</option>
                {ACTION_TYPES.map((a) => (
                  <option key={a} value={a}>
                    {actionLabel(a)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-white border border-gray-150 rounded-2xl shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="py-20 flex flex-col items-center justify-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                <span className="text-sm text-gray-500">{t('activityLog.loading')}</span>
              </div>
            ) : !data?.length ? (
              <div className="py-24 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 text-lg">{t('activityLog.emptyTitle')}</h3>
                <p className="text-gray-500 text-sm mt-1 max-w-sm mx-auto">{t('activityLog.emptyHelp')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="py-4 px-6">{t('activityLog.time')}</th>
                      <th className="py-4 px-6">{t('activityLog.user')}</th>
                      <th className="py-4 px-6">{t('activityLog.action')}</th>
                      <th className="py-4 px-6">{t('activityLog.reference')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {data.map((row) => (
                      <tr key={row.name} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 px-6 whitespace-nowrap">
                          <span className="font-medium text-gray-900 block">{row.timestamp}</span>
                          <span className="text-xs text-gray-400 font-mono">{row.name}</span>
                        </td>
                        <td className="py-4 px-6 text-gray-800">{row.user_name || row.user}</td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {actionLabel(row.action_type)}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-gray-600">{row.reference_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
